import { useGetReservationsQuery } from '@/api/squares/getReservations';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useEffect, useMemo, useState } from 'react';

const PAGE_LIMIT = 50;

interface ReservedSeatsSectionProps {
  squareId: string | null;
}

/**
 * Reserved Seats Section (DKPLAT-529) — lists users who reserved a seat for this
 * contest via the pre-questions reserve window. A row stays 'reserved' until the
 * user submits picks, at which point it converts to 'entered'.
 */
export function ReservedSeatsSection({ squareId }: ReservedSeatsSectionProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search input and reset to page 1 when it changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams = { page, limit: PAGE_LIMIT, search: debouncedSearch || undefined };

  const { data, isLoading, error } = useGetReservationsQuery(squareId, queryParams);

  const reservations = data?.reservations || [];
  const totalReservations = data?.totalReservations || 0;
  const totalPages = data?.totalPages || 1;
  const currentPage = data?.page || page;

  const statusCounts = useMemo(() => {
    let reserved = 0;
    let entered = 0;
    reservations.forEach(r => {
      if (r.status === 'entered') entered += 1;
      else reserved += 1;
    });
    return { reserved, entered };
  }, [reservations]);

  const header = (
    <div>
      <h3 className="text-lg font-semibold">Reserved Seats</h3>
      <p className="text-sm text-muted-foreground">
        Users who reserved a seat before the squares were posted
      </p>
    </div>
  );

  if (!squareId) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">Save the contest first to view reserved seats</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {header}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 font-semibold">Error loading reserved seats</p>
          <p className="text-red-500 text-sm mt-1">
            {error instanceof Error ? error.message : 'Failed to load reserved seats'}
          </p>
        </div>
      </div>
    );
  }

  const rangeStart = totalReservations === 0 ? 0 : (currentPage - 1) * PAGE_LIMIT + 1;
  const rangeEnd = Math.min(currentPage * PAGE_LIMIT, totalReservations);

  return (
    <div className="space-y-6">
      {header}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Reservations
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {totalReservations.toLocaleString()}
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Reserved (no picks yet)
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {statusCounts.reserved.toLocaleString()}
            {totalPages > 1 && <span className="text-sm font-normal"> on this page</span>}
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Entered (picks submitted)
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {statusCounts.entered.toLocaleString()}
            {totalPages > 1 && <span className="text-sm font-normal"> on this page</span>}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          type="text"
          placeholder="Search by SSO ID or username..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="pl-10"
        />
      </div>

      {totalReservations === 0 && !debouncedSearch ? (
        <div className="flex items-center justify-center py-24 border-2 border-dashed rounded-lg">
          <div className="text-center">
            <h4 className="text-xl font-semibold mb-2">No reserved seats yet</h4>
            <p className="text-muted-foreground">
              Reservations will appear here once users reserve a seat during the reserve window
            </p>
          </div>
        </div>
      ) : reservations.length === 0 ? (
        <div className="flex items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">No reservations match your search</p>
        </div>
      ) : (
        <>
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      SSO USER ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      USERNAME
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      STATUS
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      RESERVED AT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      PICKS SUBMITTED AT
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {reservations.map(reservation => (
                    <tr
                      key={reservation.reservationId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">
                        {reservation.ssoUserId || reservation.userId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {reservation.username || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {reservation.status === 'entered' ? (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                            Entered
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
                            Reserved
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {reservation.reservedAt ? (
                          <TzDateDisplay
                            utcDate={reservation.reservedAt}
                            formatString="MMM d, yyyy h:mm a"
                          />
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {reservation.picksSubmittedAt ? (
                          <TzDateDisplay
                            utcDate={reservation.picksSubmittedAt}
                            formatString="MMM d, yyyy h:mm a"
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {totalReservations === 0
                ? 'No reservations'
                : `Showing ${rangeStart.toLocaleString()}-${rangeEnd.toLocaleString()} of ${totalReservations.toLocaleString()} reservations`}
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage.toLocaleString()} of {totalPages.toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
